import React, { forwardRef, useEffect, useRef, useState } from "react";
import LaptopChromebookOutlinedIcon from "@mui/icons-material/LaptopChromebookOutlined";
import { useDispatch, useSelector } from "react-redux";
import { addMessage, setTypingMessage } from "../../store/chatSlice";
import { addMessageBenchmarking, setTypingMessageBenchmarking } from "../../store/chatSliceBenchmarking";
import TypingEffect from "../TypingEffect/TypingEffect";
import { Accordion, AccordionItem } from '@appkit4/react-components/accordion';
import { Avatar } from '@appkit4/react-components/avatar';
import { RootState } from "../../store/store";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Tooltip } from '@appkit4/react-components/tooltip';

interface Message {
  msgDateTime?: any;
  fileUploadedTime?: any;
  ResMsgDateTime?: any;
  message?: any;
  sender: "user" | "api" | "file";
  typing?: boolean;
  currentModule: string;
  response_type?: string;
  xaxis?: any;
  yaxis?: any;
  files?: any;
}

interface MessageProps {
  message: Message;
}
const Message = forwardRef<HTMLDivElement, MessageProps>(({ message }, ref) => {
  const accessToken = useSelector((state: RootState) => state.auth.accessToken)
  const [userName, setUsername] = useState('')
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const responseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (accessToken) {
      const arrayToken = accessToken.split('.');
      const tokenPayload = JSON.parse(atob(arrayToken[1]));
      setUsername(tokenPayload?.name[0])
    }
  }, [accessToken])
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  if (message.sender === "user") {
    return (
      <>
        <div ref={ref} className={`message ${message.sender}`}>
          {message.message}
          <span className="msgTime">{message.msgDateTime}</span>
          <Avatar className="profileavatar" label={userName} role="button" disabled={false}></Avatar>
        </div>
      </>
    );
  } else if (message.sender === "file") {
    return (
      <>
        <div ref={ref} className={`message ${message.sender}`}>
          <div>
            <span>You uploaded the following files:</span>
          </div>
          <div>
            {message.files.map((file: any, index: any) => (
              <div key={index} className="filename">
                <span className="Appkit4-icon icon-document-text-outline"></span>
                <span>{file.name}</span>
                <span className="fileSize">{file.size}</span>
              </div>
            ))}
          </div>
          <span className="msgTime">{message.fileUploadedTime}</span>
          <Avatar className="profileavatar" label={userName} role="button" disabled={false}></Avatar>
        </div>
      </>
    );
  }
  else {
    if (message.typing) {
      const currentMsgDate = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      return (
        <div ref={ref} className="timeMessageContainer">
          <div className={`message ${message.sender} systemmessageloading`}>
            <div className='systemicon-container'>
              <LaptopChromebookOutlinedIcon className='systemicon' />
            </div>

            <TypingEffect text={message.message} onComplete={() => {
              message.currentModule === 'idm' ? dispatch(addMessage({ message: message.message, sender: 'api', typing: false, currentModule: 'idm', ResMsgDateTime: currentMsgDate })) : dispatch(addMessageBenchmarking({ message: message.message, sender: 'api', typing: false, currentModule: 'benchmarking' }))
              if (message.message !== '**loading**') {
                if (message.currentModule === 'idm') {
                  dispatch(setTypingMessage(null))
                }
                if (message.currentModule === 'benchmarking') {
                  dispatch(setTypingMessageBenchmarking(null))
                }
              }
            }} />

          </div>
        </div>
      )

    }
    else {
      let response = JSON.parse(message.message)
      if (response.error) {
        return (
          <div ref={ref} className="timeMessageContainer">
            <div className={`message ${message.sender} systemmessage`}>
              <div className='systemicon-container'>
                <LaptopChromebookOutlinedIcon className='systemicon' />
              </div>
              <div>
                <div>
                  <p id="responceData">
                    {response.error || 'Error occured while fetching backend api call'}
                  </p>
                </div>

              </div>
            </div>
          </div>
        )
      }
      try {
        const answerJson = response.answer.match(/```json\s*([\s\S]*?)\s*```/)
        if (answerJson) {

          let parsedResponse = JSON.parse(answerJson[1])
          if (parsedResponse.answer && parsedResponse.documentName) {
            response = parsedResponse
          }
        }
      }
      catch (err) {
        response = response;
      }
      // Regular expression to check for a valid URL
      const urlPattern = /^(http|https):\/\//i;

      // Function to test the URL pattern
      const isValidUrl = (string: any) => {
        return urlPattern.test(string);
      }
      const isNotAvailable = (string: any) => {
        const lowerCaseString = string.toLowerCase();
        return lowerCaseString === 'na' || lowerCaseString === 'not available' || lowerCaseString === 'not applicable';
      }

      const onClickAccordion = (keys: string[]) => {
        setActiveKeys(keys);
      };

      const formatCellValue = (value: string, url: string) => {
        const invalidValues = ["na", "not available", "not applicable"];
        if (invalidValues.includes(value.toLowerCase())) {
          return '';
        }

        if (url !== '') {
          const urlPattern = /^(http|https):\/\/[^ "]+$/;
          if (urlPattern.test(url)) {
            return <a href={url} target="_blank" className="color" rel="noopener noreferrer">{value}</a>;
          }
        }
        return value;
      };

      const isValidField = (value: any) => {
        if (!value) return false;
        const invalidValues = ["na", "not available", "not applicable"];
        return !invalidValues.includes(value.toLowerCase());
      };
      const hasValidResponse = (response: any) => {
        return (
          isValidField(response.sourceText) ||
          isValidField(response.documentName) ||
          isValidField(response.documentUrl) ||
          isValidField(response.pageNumber) ||
          isValidField(response.documentStoragetype)
        );
      };

      const copyToClipboard = () => {
        if (responseRef.current) {
          const text = responseRef.current.innerText;
          navigator.clipboard.writeText(text)
            .then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000); // Reset after 2 sec
            })
            .catch((err) => {
              console.error("Copy failed", err);
            });
        }
      };

      return (
        <div ref={ref} className="timeMessageContainer">

          <div className={`message ${message.sender} systemmessage`}>
            <div className='systemicon-container'>
              <LaptopChromebookOutlinedIcon className='systemicon' />
            </div>
            <div style={{ width: '90%' }}>
              <div ref={responseRef}>
                <div>
                  <p id="responceData">
                    {response.answer}
                  </p>
                </div>
                <div className={hasValidResponse(response) ? "fileData" : ""}>
                  <div className="documentData">
                    {!isNotAvailable(response.sourceText) ? <span className="text-bold">Source Text: </span> : ''}
                    {!isNotAvailable(response.sourceText) ? <span>{response.sourceText}</span> : ''}
                  </div>
                  <div className="documentData">
                    {!isNotAvailable(response.documentName) ? <span className="text-bold">Document Name: </span> : ''}
                    {!isNotAvailable(response.documentName) ? <span className="color">{response.documentName}</span> : ''}
                  </div>
                  <div className="documentData">
                    {!isNotAvailable(response.documentUrl) ? <span className="text-bold">Document URL: </span> : ''}
                    {!isNotAvailable(response.documentUrl) ? (
                      isValidUrl(response.documentUrl) ? (
                        <a href={response.documentUrl} target="_blank" style={{ wordWrap: 'break-word', wordBreak: 'break-word' }} className="color" rel="noopener noreferrer">
                          {response.documentUrl}
                        </a>
                      ) : (
                        <span>{response.documentUrl}</span>
                      )
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="documentData">
                    {!isNotAvailable(response.pageNumber) ? <span className="text-bold">Page Number: </span> : ''}
                    {!isNotAvailable(response.pageNumber) ? <span>{response.pageNumber}</span> : ''}
                  </div>
                  <div className="documentData">
                    {!isNotAvailable(response.documentStoragetype) ? <span className="text-bold">Storage Type: </span> : ''}
                    {!isNotAvailable(response.documentStoragetype) ? <span>{response.documentStoragetype}</span> : ''}
                  </div>
                </div>
                {response?.documentSource?.length > 0 && (
                  <div className="accordion-panel-container">
                    <Accordion multiple={false} onClick={onClickAccordion} activeKeys={activeKeys}>
                      <AccordionItem title="Document Source" itemKey="1">
                        <div className="table-container">
                          <table className="fixed-width-table">
                            <thead>
                              <tr>
                                <th className="col-doc-name">Document Name</th>
                                <th className="col-doc-page">Page Number</th>
                              </tr>
                            </thead>
                            <tbody>
                              {response.documentSource.map((doc: any, index: any) => (
                                <tr key={index}>
                                  <td>{formatCellValue(doc.Doc_name, doc.Doc_URL)}</td>
                                  <td style={{ textAlign: 'center' }}>{formatCellValue(doc.Doc_Page, '')}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </div>
              <div>
                <Tooltip
                  trigger='hover'
                  position='top-right'
                  distance={4}
                  id="tooltipDesc"
                  content='Copy'>
                  <button
                    onClick={copyToClipboard}
                    style={{
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: 0
                    }}
                  >
                    <ContentCopyIcon
                      style={{ color:'grey', transition: 'color 0.3s ease' }}
                      fontSize="small"
                    />
                    <span
                      style={{
                        fontSize: '0.875rem',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </span>
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="resmsgTime">
            <span>{message.ResMsgDateTime}</span>
          </div>
        </div>
      )

    }
  }
});

export default Message;
